<template>
  <div class="page-wrapper">
    <DashboardHeader 
      :objectName="headerObjectName"
      @openEditProfile="openEditProfile"
      @openManageSubscription="openManageSubscription"
      @profile-updated="handleProfileUpdated"
      @notifications-read="handleNotificationsRead"
      ref="dashboardHeader"
    />
    <div class="dashboard-container">
      <!-- Toggle button for smaller screens -->
      <button 
        class="sidebar-toggle" 
        :class="{ 'sidebar-open': sidebarOpen }"
        @click="toggleSidebar"
        :aria-label="sidebarOpen ? 'Close sidebar' : 'Open sidebar'"
        :aria-expanded="sidebarOpen"
      >
        <span class="hamburger" :class="{ active: sidebarOpen }">
          <span class="line"></span>
          <span class="line"></span>
          <span class="line"></span>
        </span>
      </button>

      <!-- Sidebar -->
      <aside class="sidebar" :class="{ open: sidebarOpen }">
        <div class="sidebar-header">
          <h2 class="sidebar-title">Navigation</h2>
          <button 
            class="sidebar-close-btn" 
            @click="toggleSidebar"
            :aria-label="'Close sidebar'"
          >
            <span class="hamburger active">
              <span class="line"></span>
              <span class="line"></span>
              <span class="line"></span>
            </span>
          </button>
        </div>
        <nav class="sidebar-nav">
          <div class="nav-section">
            <ul class="nav-list">
              <li class="nav-item" :class="{ active: activeComponent === 'ObjectMainPage' }"
                @click="setActiveComponent('ObjectMainPage')">
                <div class="nav-link">
                  <div class="nav-icon">
                    <font-awesome-icon icon="chart-bar" />
                  </div>
                  <span class="nav-text">Dashboard</span>
                  <div class="nav-indicator" v-if="activeComponent === 'ObjectMainPage'"></div>
                </div>
              </li>
              <li class="nav-item" :class="{ active: activeComponent === 'ObjectSpace' }"
                @click="setActiveComponent('ObjectSpace')">
                <div class="nav-link">
                  <div class="nav-icon">
                    <font-awesome-icon icon="door-open" />
                  </div>
                  <span class="nav-text">Rooms</span>
                  <div class="nav-indicator" v-if="activeComponent === 'ObjectSpace'"></div>
                </div>
              </li>
              <li class="nav-item" :class="{ active: activeComponent === 'GuestView' }"
                @click="setActiveComponent('GuestView')">
                <div class="nav-link">
                  <div class="nav-icon">
                    <font-awesome-icon icon="users" />
                  </div>
                  <span class="nav-text">Guests</span>
                  <div class="nav-indicator" v-if="activeComponent === 'GuestView'"></div>
                </div>
              </li>
              <li class="nav-item" :class="{ active: activeComponent === 'PushNotificationView' }"
                @click="setActiveComponent('PushNotificationView')">
                <div class="nav-link">
                  <div class="nav-icon">
                    <font-awesome-icon icon="bell" />
                  </div>
                  <span class="nav-text">Push Notification</span>
                  <div class="nav-indicator" v-if="activeComponent === 'PushNotificationView'"></div>
                </div>
              </li>
              <li class="nav-item" :class="{ active: activeComponent === 'NewBookingsView' }"
                @click="setActiveComponent('NewBookingsView')">
                <div class="nav-link">
                  <div class="nav-icon">
                    <font-awesome-icon icon="calendar-check" />
                  </div>
                  <span class="nav-text">New Bookings</span>
                  <span v-if="newBookingsCount > 0" class="nav-badge">{{ newBookingsCount }}</span>
                  <div class="nav-indicator" v-if="activeComponent === 'NewBookingsView'"></div>
                </div>
              </li>
              <li class="nav-item" :class="{ active: activeComponent === 'RentView' }"
                @click="setActiveComponent('RentView')">
                <div class="nav-link">
                  <div class="nav-icon">
                    <font-awesome-icon icon="money-bill-wave" />
                  </div>
                  <span class="nav-text">Rent Payment</span>
                  <div class="nav-indicator" v-if="activeComponent === 'RentView'"></div>
                </div>
              </li>
              <li class="nav-item" :class="{ active: activeComponent === 'MaintenanceView' }"
                @click="setActiveComponent('MaintenanceView')">
                <div class="nav-link">
                  <div class="nav-icon">
                    <font-awesome-icon icon="wrench" />
                  </div>
                  <span class="nav-text">Maintenance</span>
                  <div class="nav-indicator" v-if="activeComponent === 'MaintenanceView'"></div>
                </div>
              </li>
              <li class="nav-item" :class="{ active: activeComponent === 'OutingAttendanceView' }"
                @click="setActiveComponent('OutingAttendanceView')">
                <div class="nav-link">
                  <div class="nav-icon">
                    <font-awesome-icon icon="person-walking" />
                  </div>
                  <span class="nav-text">Outing Attendance</span>
                  <div class="nav-indicator" v-if="activeComponent === 'OutingAttendanceView'"></div>
                </div>
              </li>
              <li class="nav-item" :class="{ active: activeComponent === 'MediaListView' }"
                @click="setActiveComponent('MediaListView')">
                <div class="nav-link">
                  <div class="nav-icon">
                    <font-awesome-icon icon="photo-video" />
                  </div>
                  <span class="nav-text">Media Uploads</span>
                  <div class="nav-indicator" v-if="activeComponent === 'MediaListView'"></div>
                </div>
              </li>
              <li class="nav-item" :class="{ active: activeComponent === 'FoodMenu' }"
                @click="setActiveComponent('FoodMenu')">
                <div class="nav-link">
                  <div class="nav-icon">
                    <font-awesome-icon icon="utensils" />
                  </div>
                  <span class="nav-text">Food and Menu</span>
                  <div class="nav-indicator" v-if="activeComponent === 'FoodMenu'"></div>
                </div>
              </li>
              <li class="nav-item" :class="{ active: activeComponent === 'KitchenStockDashboard' }"
                @click="setActiveComponent('KitchenStockDashboard')">
                <div class="nav-link">
                  <div class="nav-icon">
                    <font-awesome-icon icon="utensils" />
                  </div>
                  <span class="nav-text">Kitchen Stock</span>
                  <div class="nav-indicator" v-if="activeComponent === 'KitchenStockDashboard'"></div>
                </div>
              </li>
              <li class="nav-item" :class="{ active: activeComponent === 'StaffManagement' }"
                @click="setActiveComponent('StaffManagement')">
                <div class="nav-link">
                  <div class="nav-icon">
                    <font-awesome-icon icon="user-tie" />
                  </div>
                  <span class="nav-text">Staff Management</span>
                  <div class="nav-indicator" v-if="activeComponent === 'StaffManagement'"></div>
                </div>
              </li>
              <li class="nav-item nav-item-section" :class="{ expanded: expenseMenuOpen }"
                @click="toggleExpenseMenu">
                <div class="nav-link nav-link-section">
                  <div class="nav-icon">
                    <font-awesome-icon icon="wallet" />
                  </div>
                  <span class="nav-text">Expense Track</span>
                  <div class="nav-arrow">
                    <font-awesome-icon :icon="expenseMenuOpen ? 'chevron-up' : 'chevron-down'" />
                  </div>
                </div>
              </li>
              <li v-if="expenseMenuOpen" class="nav-item nav-item-sub" :class="{ active: activeComponent === 'ExpenseCategoriesView' }"
                @click="setActiveComponent('ExpenseCategoriesView')">
                <div class="nav-link">
                  <div class="nav-icon">
                    <font-awesome-icon icon="tags" />
                  </div>
                  <span class="nav-text">Categories</span>
                  <div class="nav-indicator" v-if="activeComponent === 'ExpenseCategoriesView'"></div>
                </div>
              </li>
              <li v-if="expenseMenuOpen" class="nav-item nav-item-sub" :class="{ active: activeComponent === 'ExpenseTransactionsView' }"
                @click="setActiveComponent('ExpenseTransactionsView')">
                <div class="nav-link">
                  <div class="nav-icon">
                    <font-awesome-icon icon="receipt" />
                  </div>
                  <span class="nav-text">Transactions</span>
                  <div class="nav-indicator" v-if="activeComponent === 'ExpenseTransactionsView'"></div>
                </div>
              </li>
              <li v-if="expenseMenuOpen" class="nav-item nav-item-sub" :class="{ active: activeComponent === 'ExpenseReportsView' }"
                @click="setActiveComponent('ExpenseReportsView')">
                <div class="nav-link">
                  <div class="nav-icon">
                    <font-awesome-icon icon="chart-pie" />
                  </div>
                  <span class="nav-text">Reports</span>
                  <div class="nav-indicator" v-if="activeComponent === 'ExpenseReportsView'"></div>
                </div>
              </li>
            </ul>
          </div>
        </nav>
      </aside>

      <!-- Overlay -->
      <div class="overlay" :class="{ open: sidebarOpen }" @click="toggleSidebar"></div>

      <!-- Main Content -->
      <main class="main-content">
        <component 
          :is="activeComponent" 
          :objectSpaces="objectSpaces" 
          :objectData="objectData" 
          :isLoading="isLoading"
          @notifications-read="handleNotificationsRead"
          @rentcron-updated="handleRentCronUpdated"
        />
      </main>
    </div>

    <!-- Edit Profile Modal -->
    <EditProfile 
      v-if="showEditProfile" 
      :admin-profile-data="adminProfileData"
      @close="closeEditProfile"
      @profile-updated="handleProfileUpdated"
    />

    <ManageSubscription
      v-if="showManageSubscription"
      :admin-profile-data="adminProfileData"
      @close="closeManageSubscription"
    />
  </div>
</template>

<script>
import DashboardHeader from './DashboardHeader.vue';
import ObjectMainPage from './ObjectMainPage.vue';
import ObjectSpace from './ObjectSpace.vue';
import GuestView from './GuestView.vue';
import PushNotificationView from './PushNotificationView.vue';
import NewBookingsView from './NewBookingsView.vue';
import RentView from './RentView.vue';
import MaintenanceView from './MaintenanceView.vue';
import OutingAttendanceView from './OutingAttendanceView.vue';
import MediaListView from './MediaListView.vue';
import FoodMenu from './FoodMenu.vue';
import KitchenStockDashboard from './KitchenStockDashboard.vue';
import StaffManagement from './StaffManagement.vue';
import ExpenseCategoriesView from './ExpenseCategoriesView.vue';
import ExpenseTransactionsView from './ExpenseTransactionsView.vue';
import ExpenseReportsView from './ExpenseReportsView.vue';
import EditProfile from './EditProfile.vue';
import ManageSubscription from './ManageSubscription.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faChartBar, faUsers, faDoorOpen, faMoneyBillWave, faWrench, faPhotoVideo, faUtensils, faUserTie, faCalendarCheck, faBell, faPersonWalking, faWallet, faTags, faReceipt, faChartPie, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import apiService from '@/services/api';
import { isNoDataError, isHmsSubscriptionInactiveError } from '../utilities/errorHelper';
import notificationService from '@/services/notificationService';

library.add(faChartBar, faUsers, faDoorOpen, faMoneyBillWave, faWrench, faPhotoVideo, faUtensils, faUserTie, faCalendarCheck, faBell, faPersonWalking, faWallet, faTags, faReceipt, faChartPie, faChevronDown, faChevronUp);

const TABS_WITHOUT_OBJECT_CONTEXT = new Set(['ObjectMainPage']);

export default {
  name: 'DashboardPage',
    components: {
        DashboardHeader,
        FontAwesomeIcon,
        ObjectMainPage,
        ObjectSpace,
        GuestView,
        PushNotificationView,
        NewBookingsView,
        RentView,
        MaintenanceView,
        OutingAttendanceView,
        MediaListView,
        FoodMenu,
        KitchenStockDashboard,
        StaffManagement,
        ExpenseCategoriesView,
        ExpenseTransactionsView,
        ExpenseReportsView,
        EditProfile,
        ManageSubscription,
    },
  data() {
    return {
      sidebarOpen: false,
      activeComponent: this.getActiveComponentFromRoute(), // Get from URL or default
      objectSpaces: [],
      isLoading: false,
      objectData: {},
      objectContextLoaded: false,
      showEditProfile: false,
      showManageSubscription: false,
      adminProfileData: {},
      newBookingsCount: 0,
      notificationUpdateKey: 0,
      expenseMenuOpen: ['ExpenseCategoriesView', 'ExpenseTransactionsView', 'ExpenseReportsView'].includes(this.getActiveComponentFromRoute())
    };
  },
  computed: {
    headerObjectName() {
      const fromApi = this.objectData?.objectName;
      if (fromApi && String(fromApi).trim()) return String(fromApi).trim();
      const raw = this.$route.params?.objectName;
      if (raw == null || raw === '') return '';
      try {
        return decodeURIComponent(String(raw));
      } catch {
        return String(raw);
      }
    },
  },
  methods: {
    toggleSidebar() {
      this.sidebarOpen = !this.sidebarOpen;
    },
    async setActiveComponent(componentName) {
      this.activeComponent = componentName;
      if (['ExpenseCategoriesView', 'ExpenseTransactionsView', 'ExpenseReportsView'].includes(componentName)) {
        this.expenseMenuOpen = true;
      }
      // Update URL with active component
      this.updateUrlWithActiveComponent(componentName);
      if (!TABS_WITHOUT_OBJECT_CONTEXT.has(componentName)) {
        await this.ensureObjectContext();
      }
      // Mark notifications as read immediately to remove badge counts
      // But NEW tags will remain until page refresh
      if (componentName === 'NewBookingsView') {
        // Store newBookingIds snapshot before marking as read
        // This ensures NEW tags remain visible even after Read API is called
        const objectId = this.$route.params.id;
        if (objectId) {
          const newBookingIds = notificationService.getNewBookingIds(objectId);
          // Store in sessionStorage so NewBookingsView can use it
          sessionStorage.setItem(`NEW_BOOKING_IDS_${objectId}`, JSON.stringify(newBookingIds));
        }
        await this.markNotificationsAsRead();
      }
      if (this.sidebarOpen) {
        this.toggleSidebar(); // Close sidebar on mobile after selection
      }
    },
    
    async markNotificationsAsRead() {
      try {
        const objectId = this.$route.params.id;
        if (objectId && this.newBookingsCount > 0) {
          await apiService.markBookingNotificationAsRead(objectId);
          // Refresh notifications from service after marking as read
          await notificationService.refreshNotifications();
          // Update local count (will be updated via subscription, but update immediately)
          this.updateNotificationCount();
          // Also refresh DashboardHeader notifications
          if (this.$refs.dashboardHeader) {
            await this.$refs.dashboardHeader.refreshNotifications();
          }
        }
      } catch (error) {
        if (isNoDataError(error) || isHmsSubscriptionInactiveError(error)) {
          return;
        }
        console.error('Error marking notifications as read:', error);
      }
    },
    
    async handleNotificationsRead() {
      // When notifications are marked as read, refresh from service
      await notificationService.refreshNotifications();
      // Update local counts
      await this.fetchBookingNotifications();
      // Also refresh DashboardHeader notifications
      if (this.$refs.dashboardHeader) {
        await this.$refs.dashboardHeader.refreshNotifications();
      }
    },
    getActiveComponentFromRoute() {
      // Get active component from URL query parameter, default to ObjectMainPage
      const query = this.$route.query;
      const validComponents = ['ObjectMainPage', 'ObjectSpace', 'GuestView', 'PushNotificationView', 'NewBookingsView', 'RentView', 'MaintenanceView', 'OutingAttendanceView', 'MediaListView', 'FoodMenu', 'KitchenStockDashboard', 'StaffManagement', 'ExpenseCategoriesView', 'ExpenseTransactionsView', 'ExpenseReportsView'];
      
      if (query.tab && validComponents.includes(query.tab)) {
        return query.tab;
      }
      
      return 'ObjectMainPage'; // Default fallback
    },
    updateUrlWithActiveComponent(componentName) {
      // Update URL without triggering a page reload
      const currentRoute = this.$route;
      if (currentRoute.query.tab !== componentName) {
        this.$router.replace({
          path: currentRoute.path,
          query: { ...currentRoute.query, tab: componentName }
        });
      }
    },
    async fetchObjectSpaces() {
      let objectId = this.$route.params.id;
      try {
        let response = await apiService.getObjectSpace({ objectId });
        this.objectSpaces = response.data?.data || [];
      } catch (error) {
        if (isNoDataError(error)) {
          // No data available, set empty array
          this.objectSpaces = [];
        } else if (isHmsSubscriptionInactiveError(error)) {
          // Subscription inactive — modal shown in App.vue
        } else {
          console.error('Failed to fetch object spaces:', error.response?.data);
          throw error;
        }
      }
    },
    async handleRentCronUpdated() {
      await this.fetchObject();
    },

    async fetchObject() {
      let objectId = this.$route.params.id;
      try {
        let response = await apiService.getObject(objectId);
        this.objectData = response.data || {};
      } catch (error) {
        if (isNoDataError(error)) {
          // No data available, set empty object
          this.objectData = {};
        } else if (isHmsSubscriptionInactiveError(error)) {
          // Subscription inactive — modal shown in App.vue
        } else {
          console.error('Failed to fetch object:', error.response?.data);
          throw error;
        }
      }
    },
    async ensureObjectContext() {
      if (this.objectContextLoaded) return;
      this.isLoading = true;
      try {
        await Promise.all([
          this.fetchObjectSpaces().catch((e) => {
            if (!isNoDataError(e) && !isHmsSubscriptionInactiveError(e)) {
              console.error('Failed to fetch object spaces:', e.response?.data);
            }
          }),
          this.fetchObject().catch((e) => {
            if (!isNoDataError(e) && !isHmsSubscriptionInactiveError(e)) {
              console.error('Failed to fetch object:', e.response?.data);
            }
          }),
        ]);
        this.objectContextLoaded = true;
      } finally {
        this.isLoading = false;
      }
    },
    openEditProfile() {
      this.showEditProfile = true;
      document.body.style.overflow = 'hidden';
    },
    closeEditProfile() {
      this.showEditProfile = false;
      document.body.style.overflow = 'auto';
    },
    openManageSubscription() {
      this.showManageSubscription = true;
      document.body.style.overflow = 'hidden';
    },
    closeManageSubscription() {
      this.showManageSubscription = false;
      document.body.style.overflow = 'auto';
    },
    handleProfileUpdated(updatedData) {
      this.adminProfileData = updatedData;
      // Update the DashboardHeader's adminProfileData directly
      if (this.$refs.dashboardHeader) {
        this.$refs.dashboardHeader.adminProfileData = updatedData;
      }
    },
    loadAdminProfileData() {
      try {
        const storedData = localStorage.getItem('ADMIN_PROFILE_DATA');
        this.adminProfileData = storedData ? JSON.parse(storedData) : {};
      } catch (error) {
        console.error('Failed to parse admin profile data:', error);
        this.adminProfileData = {};
      }
    },
    async fetchBookingNotifications(forceRefresh = false) {
      const objectId = this.$route.params.id;
      if (objectId) {
        // Get from centralized notification service
        await notificationService.getNotifications(forceRefresh);
        this.updateNotificationCount();
      } else {
        this.newBookingsCount = 0;
      }
    },
    
    updateNotificationCount() {
      const objectId = this.$route.params.id;
      if (objectId) {
        this.newBookingsCount = notificationService.getNotificationCount(objectId);
      } else {
        this.newBookingsCount = 0;
      }
    },
    toggleExpenseMenu() {
      this.expenseMenuOpen = !this.expenseMenuOpen;
    },
  },
  watch: {
    // Watch for route changes to update active component
    '$route'(to, from) {
      if (to.query.tab !== from.query.tab) {
        this.activeComponent = this.getActiveComponentFromRoute();
        if (['ExpenseCategoriesView', 'ExpenseTransactionsView', 'ExpenseReportsView'].includes(this.activeComponent)) {
          this.expenseMenuOpen = true;
        }
        if (!TABS_WITHOUT_OBJECT_CONTEXT.has(this.activeComponent)) {
          void this.ensureObjectContext();
        }
      }
    },
  },
  async mounted() {
    this.loadAdminProfileData();
    if (!TABS_WITHOUT_OBJECT_CONTEXT.has(this.activeComponent)) {
      await this.ensureObjectContext();
    }
    // Load notifications from service (force refresh on page load)
    await this.fetchBookingNotifications(true);
    
    // Subscribe to notification updates
    this.unsubscribeNotifications = notificationService.subscribe(() => {
      this.notificationUpdateKey++;
      this.updateNotificationCount();
    });
  },
  
  beforeUnmount() {
    // Clean up body overflow when component is destroyed
    document.body.style.overflow = 'auto';
    // Unsubscribe from notifications
    if (this.unsubscribeNotifications) {
      this.unsubscribeNotifications();
    }
  },
};
</script>

<style scoped>
.page-wrapper {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.dashboard-container {
  /* display: flex; */
  flex: 1;
  background-color: #f5f5f5;
  margin-top: 72px;
  overflow-x: hidden;
  /* Prevent horizontal scrolling for the entire dashboard */
}

/* Sidebar Styles */
.sidebar {
  position: fixed;
  top: 80px;
  left: 0;
  width: 260px;
  height: calc(100vh - 80px);
  background: linear-gradient(180deg, #5b2e8e 0%, #4a2575 100%);
  color: white;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 100;
  overflow-y: auto;
  overflow-x: hidden;
  box-shadow: 4px 0 20px rgba(91, 46, 142, 0.15);
}

/* Sidebar Header */
.sidebar-header {
  padding: 24px 20px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
}

.sidebar-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  color: #ffffff;
  letter-spacing: 0.5px;
  flex: 1;
}

/* Sidebar Close Button (inside sidebar) */
.sidebar-close-btn {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 8px;
  cursor: pointer;
  color: #ffffff;
  display: none;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  width: 36px;
  height: 36px;
  flex-shrink: 0;
}

.sidebar-close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
  transform: scale(1.05);
}

.sidebar-close-btn:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.3);
}

.sidebar-close-btn .hamburger {
  width: 18px;
  height: 14px;
}

.sidebar-nav {
  height: calc(100% - 80px);
  padding: 24px 0;
  overflow-y: auto;
  overflow-x: hidden;
}

.nav-section {
  min-height: 100%;
}

.nav-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 0 16px;
}

.nav-item {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.nav-item-section {
  margin-top: 8px;
  opacity: 0.95;
}

.nav-link-section {
  cursor: pointer;
  background: transparent;
  font-weight: 700;
}

.nav-item-section.expanded .nav-link-section {
  background: rgba(255, 255, 255, 0.08);
}

.nav-arrow {
  margin-left: auto;
  margin-right: 8px;
  font-size: 12px;
  opacity: 0.9;
}

.nav-item-sub .nav-link {
  padding-left: 30px;
}

.nav-link {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 16px 20px;
  cursor: pointer;
  font-size: 15px;
  font-weight: 500;
  text-decoration: none;
  color: rgba(255, 255, 255, 0.85);
  position: relative;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  user-select: none;
  text-align: left;
}

.nav-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-right: 16px;
  font-size: 16px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.nav-text {
    flex: 1;
    font-weight: 500;
    letter-spacing: 0.01em;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.nav-badge {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    color: white;
    border-radius: 12px;
    min-width: 18px;
    height: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 700;
    padding: 0 5px;
    box-shadow: 0 2px 6px rgba(239, 68, 68, 0.35), 0 0 0 2px rgba(255, 255, 255, 0.1);
    border: none;
    line-height: 1;
    margin-left: auto;
    margin-right: 8px;
    letter-spacing: -0.2px;
}

.nav-indicator {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 24px;
  background: white;
  border-radius: 2px 0 0 2px;
  animation: slideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Hover Effects */
.nav-item:hover .nav-link {
  background: rgba(255, 255, 255, 0.08);
  color: white;
  transform: translateX(4px);
}

.nav-item:hover .nav-icon {
  transform: scale(1.1);
  color: white;
}

/* Active State */
.nav-item.active .nav-link {
  background: rgba(255, 255, 255, 0.12);
  color: white;
  font-weight: 600;
}

.nav-item.active .nav-icon {
  color: white;
  transform: scale(1.05);
}

.nav-item.active .nav-text {
  font-weight: 600;
}

/* Focus States for Accessibility */
.nav-item:focus-within .nav-link {
  outline: 2px solid rgba(255, 255, 255, 0.3);
  outline-offset: 2px;
}

/* Animation Keyframes */
@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-50%) translateX(10px);
  }
  to {
    opacity: 1;
    transform: translateY(-50%) translateX(0);
  }
}

.main-content {
  margin-left: 260px;
  flex: none;
  display: block;
  background-color: #ffffff;
  position: relative;
  overflow-x: hidden;
  overflow-y: auto;
  height: calc(100vh - 72px);
  transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.sidebar-toggle {
  display: none;
  position: fixed;
  top: 100px;
  left: 20px;
  z-index: 999;
  background: #ffffff;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px;
  font-size: 16px;
  cursor: pointer;
  color: #5b2e8e;
  box-shadow: 0 4px 12px rgba(91, 46, 142, 0.15);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  align-items: center;
  justify-content: center;
}

.sidebar-toggle.sidebar-open {
  display: none;
}

.sidebar-toggle:hover {
  background: #5b2e8e;
  color: #ffffff;
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(91, 46, 142, 0.25);
}

.sidebar-toggle:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(91, 46, 142, 0.2);
}

/* Hamburger Animation */
.hamburger {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 20px;
  height: 16px;
  transition: all 0.3s ease;
}

.hamburger .line {
  width: 100%;
  height: 2px;
  background-color: currentColor;
  border-radius: 2px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform-origin: center;
}

.hamburger.active .line:nth-child(1) {
  transform: translateY(7px) rotate(45deg);
}

.hamburger.active .line:nth-child(2) {
  opacity: 0;
  transform: scale(0);
}

.hamburger.active .line:nth-child(3) {
  transform: translateY(-7px) rotate(-45deg);
}

/* Responsive Design */
@media (max-width: 1024px) {
  .sidebar-toggle {
    display: flex;
  }

  .sidebar-toggle.sidebar-open {
    display: none;
  }

  .sidebar-close-btn {
    display: flex;
  }

  .sidebar {
    position: fixed;
    top: 80px;
    left: -260px;
    width: 260px;
    height: calc(100vh - 80px);
    z-index: 1000;
    transition: left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .sidebar.open {
    left: 0;
  }

  .main-content {
    width: 100%;
    margin-left: 0;
  }
}

/* Mobile Responsive Styles */
@media (max-width: 768px) {
  .sidebar {
    position: fixed;
    top: 0;
    left: -260px;
    height: 100vh;
    overflow-y: auto;
    overflow-x: hidden;
  }

  .sidebar.open {
    left: 0;
  }

  .dashboard-container {
    margin-top: 72px;
  }

  .sidebar-toggle {
    top: 85px;
    left: 20px;
  }

  .sidebar-toggle.sidebar-open {
    display: none;
  }

  .sidebar-close-btn {
    display: flex;
  }

  .overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(2px);
    z-index: 999;
    display: none;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .overlay.open {
    display: block;
  }

  .main-content {
    margin-left: 0;
    width: 100%;
    height: calc(100vh - 72px);
  }

  /* Enhance mobile nav items */
  .nav-link {
    padding: 18px 20px;
    font-size: 16px;
  }

  .nav-icon {
    width: 22px;
    height: 22px;
    font-size: 18px;
  }

  .nav-item:hover .nav-link {
    transform: translateX(0);
  }
}

@media (max-width: 480px) {
  .sidebar {
    width: 240px;
    left: -240px;
  }
  
  .sidebar.open {
    left: 0;
  }
  
  .sidebar-toggle {
    top: 85px;
    left: 15px;
    padding: 10px;
  }

  .sidebar-toggle.sidebar-open {
    display: none;
  }

  .sidebar-close-btn {
    display: flex;
  }
}

@media (max-width: 360px) {
  .sidebar {
    width: 220px;
    left: -220px;
  }
  
  .sidebar.open {
    left: 0;
  }
  
  .sidebar-toggle {
    top: 80px;
    left: 10px;
    padding: 8px;
  }

  .sidebar-toggle.sidebar-open {
    display: none;
  }

  .sidebar-close-btn {
    display: flex;
  }
  
  .hamburger {
    width: 18px;
    height: 14px;
  }
}
</style>